import { formatDuration, Reporting } from '@local/common-lib';
import {
  Download as DownloadIcon,
  EventBusy as NoActivityIcon,
  TableChart as ReportsIcon,
} from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { DateTime, Duration } from 'luxon';
import type { FC } from 'react';
import { useState } from 'react';
import { CenterLayout } from '../../components/page-layout/index.js';
import { useAxios } from '../../hooks/use-axios.js';
import {
  downloadMonthlySummaryCsv,
  downloadMonthlySummaryXlsx,
  monthlySummaryColumns,
  monthlySummaryDisplayValue,
} from './monthly-summary-export.js';
import {
  downloadYearlyPatrollerSummaryCsv,
  downloadYearlyPatrollerSummaryXlsx,
  yearlyPatrollerSummaryDisplayValue,
} from './yearly-patroller-summary-export.js';

const previousMonth = DateTime.now()
  .setZone(Reporting.NZ_TIME_ZONE)
  .minus({ months: 1 })
  .toFormat('yyyy-LL');
const currentYear = DateTime.now()
  .setZone(Reporting.NZ_TIME_ZONE)
  .toFormat('yyyy');

type ReportTab = 'monthly' | 'yearly';

export const formatReportDuration = (duration: string | undefined): string => {
  const [hours, minutes, seconds] = duration?.split(':').map(Number) ?? [
    0, 0, 0,
  ];
  return formatDuration(Duration.fromObject({ hours, minutes, seconds }));
};

export const ReportTabs: FC<{
  active: ReportTab;
  onChange: (report: ReportTab) => void;
}> = ({ active, onChange }) => (
  <Tabs
    aria-label="Reports"
    onChange={(_, report: ReportTab) => onChange(report)}
    value={active}
    variant="scrollable"
  >
    <Tab label="Monthly Summary" value="monthly" />
    <Tab label="Yearly Patroller Summary" value="yearly" />
  </Tabs>
);

const SummaryTable: FC<{ report: Reporting.MonthlySummary }> = ({ report }) => (
  <TableContainer sx={{ maxWidth: '100%', overflowX: 'auto' }}>
    <Table size="small" sx={{ minWidth: 1900 }}>
      <TableHead>
        <TableRow>
          {monthlySummaryColumns.map((column) => (
            <TableCell key={column.key} sx={{ whiteSpace: 'nowrap' }}>
              {column.label}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {report.rows.map((row, index) => (
          <TableRow
            key={`${row.date}-${row.primary}-${
              // biome-ignore lint/suspicious/noArrayIndexKey: required here
              index
            }`}
          >
            {monthlySummaryColumns.map((column) => (
              <TableCell key={column.key} sx={{ whiteSpace: 'nowrap' }}>
                {column.key === 'duration'
                  ? formatReportDuration(row.duration)
                  : monthlySummaryDisplayValue(column.key, row[column.key])}
              </TableCell>
            ))}
          </TableRow>
        ))}
        <TableRow sx={{ '& td': { fontWeight: 'bold' } }}>
          {monthlySummaryColumns.map((column) => (
            <TableCell key={column.key} sx={{ whiteSpace: 'nowrap' }}>
              {column.key === 'duration'
                ? formatReportDuration(report.totals.duration)
                : (report.totals[column.key] ?? '')}
            </TableCell>
          ))}
        </TableRow>
      </TableBody>
    </Table>
  </TableContainer>
);

export const EmptyMonthlySummary: FC<{ month: string }> = ({ month }) => {
  const monthLabel = DateTime.fromFormat(month, 'yyyy-LL', {
    zone: Reporting.NZ_TIME_ZONE,
  }).toFormat('LLLL yyyy');
  return (
    <Card>
      <CardContent>
        <Stack
          alignItems="center"
          spacing={1}
          sx={{ py: 4, textAlign: 'center' }}
        >
          <NoActivityIcon color="disabled" sx={{ fontSize: 48 }} />
          <Typography variant="h6">No activity recorded</Typography>
          <Typography color="text.secondary">
            No patrols or events were scheduled in {monthLabel}. Select another
            calendar month to view its summary.
          </Typography>
        </Stack>
      </CardContent>
    </Card>
  );
};

const YearlyPatrollerSummaryTable: FC<{
  report: Reporting.YearlyPatrollerSummary;
}> = ({ report }) => (
  <TableContainer sx={{ maxWidth: '100%', overflowX: 'auto' }}>
    <Table size="small" sx={{ minWidth: 650 }}>
      <TableHead>
        <TableRow>
          <TableCell>Patroller</TableCell>
          <TableCell align="right">Primary Patroller</TableCell>
          <TableCell align="right">Patroller</TableCell>
          <TableCell align="right">Total Patrols</TableCell>
          <TableCell align="right">Time Patrolling</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {report.rows.map((row) => (
          <TableRow key={row.patroller}>
            <TableCell>{row.patroller}</TableCell>
            <TableCell align="right">
              {yearlyPatrollerSummaryDisplayValue(
                'primaryPatroller',
                row.primaryPatroller,
              )}
            </TableCell>
            <TableCell align="right">
              {yearlyPatrollerSummaryDisplayValue(
                'patrollerCount',
                row.patrollerCount,
              )}
            </TableCell>
            <TableCell align="right">
              {yearlyPatrollerSummaryDisplayValue(
                'totalPatrols',
                row.totalPatrols,
              )}
            </TableCell>
            <TableCell align="right">
              {formatReportDuration(row.duration)}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

const EmptyYearlyPatrollerSummary: FC<{ year: string }> = ({ year }) => (
  <Card>
    <CardContent>
      <Stack
        alignItems="center"
        spacing={1}
        sx={{ py: 4, textAlign: 'center' }}
      >
        <NoActivityIcon color="disabled" sx={{ fontSize: 48 }} />
        <Typography variant="h6">No completed patrols recorded</Typography>
        <Typography color="text.secondary">
          No completed patrols were recorded in {year}. Select another calendar
          year to view its summary.
        </Typography>
      </Stack>
    </CardContent>
  </Card>
);

export const ReportsPage: FC = () => {
  const [activeReport, setActiveReport] = useState<ReportTab>('monthly');
  const [month, setMonth] = useState(previousMonth);
  const [requestedMonth, setRequestedMonth] = useState<string>();
  const [year, setYear] = useState(currentYear);
  const [requestedYear, setRequestedYear] = useState<string>();
  const [xlsxLoading, setXlsxLoading] = useState(false);
  const [result, refresh] = useAxios<Reporting.MonthlySummary>(
    `/api/v1/reports/monthly-summary/${encodeURIComponent(requestedMonth ?? previousMonth)}`,
    undefined,
    { condition: !!requestedMonth },
  );
  const [yearResult, refreshYear] = useAxios<Reporting.YearlyPatrollerSummary>(
    `/api/v1/reports/yearly-patroller-summary/${encodeURIComponent(requestedYear ?? currentYear)}`,
    undefined,
    { condition: !!requestedYear },
  );

  const viewSummary = (): void => {
    if (requestedMonth === month) refresh();
    else setRequestedMonth(month);
  };
  const viewYearlySummary = (): void => {
    if (requestedYear === year) refreshYear();
    else setRequestedYear(year);
  };
  const downloadXlsx = async (
    report: Reporting.MonthlySummary,
  ): Promise<void> => {
    setXlsxLoading(true);
    try {
      await downloadMonthlySummaryXlsx(report, requestedMonth ?? month);
    } finally {
      setXlsxLoading(false);
    }
  };

  return (
    <CenterLayout>
      <Stack spacing={3} sx={{ width: '100%' }}>
        <Card>
          <CardHeader avatar={<ReportsIcon />} title="Reports" />
          <CardContent>
            <Typography color="text.secondary">
              Select a report and its reporting period.
            </Typography>
          </CardContent>
        </Card>
        <Card>
          <ReportTabs active={activeReport} onChange={setActiveReport} />
        </Card>
        {activeReport === 'monthly' ? (
          <>
            <Card>
              <CardHeader title="Monthly Summary" />
              <CardContent>
                <Stack direction={{ sm: 'row', xs: 'column' }} spacing={2}>
                  <TextField
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ max: previousMonth }}
                    label="Calendar month"
                    onChange={(event) => setMonth(event.target.value)}
                    type="month"
                    value={month}
                  />
                  <LoadingButton
                    disabled={!month || month > previousMonth}
                    loading={result.loading && requestedMonth === month}
                    onClick={viewSummary}
                    variant="contained"
                  >
                    View Summary
                  </LoadingButton>
                </Stack>
              </CardContent>
            </Card>
            {result.error ? (
              <Alert severity="error">Unable to load this report.</Alert>
            ) : null}
            {result.data && result.data.rows.length ? (
              <Card>
                <CardHeader
                  action={
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        onClick={() =>
                          downloadMonthlySummaryCsv(
                            result.data,
                            requestedMonth ?? month,
                          )
                        }
                        startIcon={<DownloadIcon />}
                      >
                        CSV
                      </Button>
                      <LoadingButton
                        loading={xlsxLoading}
                        onClick={() => void downloadXlsx(result.data)}
                        startIcon={<DownloadIcon />}
                      >
                        Excel
                      </LoadingButton>
                    </Box>
                  }
                  title={`Monthly Summary — ${requestedMonth}`}
                />
                <CardContent>
                  <SummaryTable report={result.data} />
                </CardContent>
              </Card>
            ) : null}
            {result.data && !result.data.rows.length ? (
              <EmptyMonthlySummary month={requestedMonth ?? month} />
            ) : null}
          </>
        ) : null}
        {activeReport === 'yearly' ? (
          <>
            <Card>
              <CardHeader title="Yearly Patroller Summary" />
              <CardContent>
                <Stack direction={{ sm: 'row', xs: 'column' }} spacing={2}>
                  <TextField
                    inputProps={{ max: currentYear, min: 0, step: 1 }}
                    label="Calendar year"
                    onChange={(event) => setYear(event.target.value)}
                    type="number"
                    value={year}
                  />
                  <LoadingButton
                    disabled={!/^\d{4}$/.test(year) || year > currentYear}
                    loading={yearResult.loading && requestedYear === year}
                    onClick={viewYearlySummary}
                    variant="contained"
                  >
                    View Summary
                  </LoadingButton>
                </Stack>
              </CardContent>
            </Card>
            {yearResult.error ? (
              <Alert severity="error">Unable to load this report.</Alert>
            ) : null}
            {yearResult.data && yearResult.data.rows.length ? (
              <Card>
                <CardHeader
                  action={
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        onClick={() =>
                          downloadYearlyPatrollerSummaryCsv(
                            yearResult.data,
                            requestedYear ?? year,
                          )
                        }
                        startIcon={<DownloadIcon />}
                      >
                        CSV
                      </Button>
                      <Button
                        onClick={() =>
                          downloadYearlyPatrollerSummaryXlsx(
                            yearResult.data,
                            requestedYear ?? year,
                          )
                        }
                        startIcon={<DownloadIcon />}
                      >
                        Excel
                      </Button>
                    </Box>
                  }
                  title={`Yearly Patroller Summary — ${requestedYear}`}
                />
                <CardContent>
                  <YearlyPatrollerSummaryTable report={yearResult.data} />
                </CardContent>
              </Card>
            ) : null}
            {yearResult.data && !yearResult.data.rows.length ? (
              <EmptyYearlyPatrollerSummary year={requestedYear ?? year} />
            ) : null}
          </>
        ) : null}
      </Stack>
    </CenterLayout>
  );
};
